// Type-only imports so `{@link …}` JSDoc references below resolve.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type DetailsSidebar } from './DetailsSidebar';
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Grid } from './Grid';
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Sidebar, SidebarProvider } from './Sidebar';
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Stack } from './Stack';
import { TooltipProvider } from './Tooltip';
import { VariantProps, tv } from './utils/cn';

const layoutStyles = tv({
  base: '@container flex min-h-svh w-full overflow-y-hidden bg-bg-secondary',
  variants: {
    type: {
      default: '',
      simple: 'pl-3',
    },
  },
  defaultVariants: {
    type: 'default',
  },
});

type LayoutStyleProps = VariantProps<typeof layoutStyles>;

/**
 * Top-level shape of a {@link Layout} page shell.
 *
 * - `'default'`: renders the {@link SidebarProvider} and expects a
 *   {@link Sidebar} slot followed by a {@link Main} slot as direct
 *   children. Use for the standard signed-in app chrome.
 * - `'simple'`: skips the sidebar provider and wraps children in a
 *   {@link Main} for you, so compose directly with {@link Header},
 *   {@link Content}, and {@link Footer} (do not pass a `<Main>`
 *   yourself). Use for sign-in screens, full-bleed onboarding, or any
 *   page that does not need primary navigation.
 *
 * @default 'default'
 */
export type LayoutType = NonNullable<LayoutStyleProps['type']>;

/**
 * Props for the {@link Layout} component.
 *
 * Layout owns the page shell. It does not forward arbitrary `<div>` props;
 * wrap or compose if you need ref / event handlers / className passthrough.
 */
export type LayoutProps = {
  /**
   * Shape depends on `type`:
   *
   * - `type='default'`: a {@link Sidebar} slot (optional) plus a
   *   {@link Main} subtree.
   * - `type='simple'`: the inner regions directly ({@link Header},
   *   {@link Content}, {@link Footer}). Layout wraps them in a
   *   {@link Main} for you.
   */
  children: React.ReactNode;
  /**
   * Page-shell variant. `'default'` includes the sidebar provider;
   * `'simple'` drops it and renders Main for you. See {@link LayoutType}.
   */
  type?: LayoutType;
};

/**
 * Page-shell primitive: full-viewport application chrome that hosts a
 * {@link Sidebar} slot plus a {@link Main} content area.
 *
 * Layout sits at the top of the layout-prop stack. The shell owns the
 * viewport-sized container (`min-h-svh`, container background, sidebar
 * slot); {@link Main} owns the inner CSS Grid template that positions
 * {@link Header}, {@link Content}, {@link DetailsSidebar}, and
 * {@link Footer}; {@link Content} owns the inner container width and
 * padding. Each layer is independent: change the page chrome without
 * touching content sizing, and vice versa.
 *
 * Responsive behavior lives inside {@link Main}: below the `md`
 * breakpoint the main area fills edge to edge; from `md` up it pulls
 * in with a margin and rounds its border to create the inset card look.
 *
 * See also: {@link Grid} for inline 2 dimensional layouts; {@link Stack}
 * for 1 dimensional layouts, row/column flows; {@link Sidebar} and
 * {@link DetailsSidebar} for the navigation slots that compose with this shell.
 *
 * @example
 * // Standard app shell: sidebar + main with header, content, footer.
 * <Layout>
 *   <Sidebar>{navItems}</Sidebar>
 *   <Main>
 *     <Header>{pageTitle}</Header>
 *     <Content>{pageBody}</Content>
 *     <Footer>{statusBar}</Footer>
 *   </Main>
 * </Layout>
 *
 * @example
 * // Simple shell: no sidebar. Layout wraps the children in Main for you,
 * // so compose Header / Content / Footer directly (no explicit `<Main>`).
 * <Layout type="simple">
 *   <Header>{slimHeader}</Header>
 *   <Content size="sm" padded>{signInForm}</Content>
 * </Layout>
 *
 * @example
 * // Constrained reading width with extra responsive padding via a wrapper.
 * <Main>
 *   <Header>{pageTitle}</Header>
 *   <Content size="xl">
 *     <div className="p-3 md:p-6 lg:p-8">
 *       <Dashboard />
 *     </div>
 *   </Content>
 * </Main>
 */
export function Layout({ children, type }: LayoutProps) {
  const className = layoutStyles({ type });

  if (type === 'simple') {
    return (
      <TooltipProvider>
        <div className={className}>
          <Main>{children}</Main>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <SidebarProvider>
        <div className={className}>{children}</div>
      </SidebarProvider>
    </TooltipProvider>
  );
}

/** Props for the {@link Main} component. */
export type MainProps = {
  /** Composed of {@link Header}, {@link Content}, {@link Footer}, and an optional {@link DetailsSidebar}. */
  children: React.ReactNode;
};

/**
 * Inner page region that owns the CSS Grid template positioning
 * {@link Header}, {@link Content}, {@link DetailsSidebar}, and
 * {@link Footer}.
 *
 * The grid is `grid-cols-[1fr_auto] grid-rows-[auto_1fr_auto]`: header
 * spans both columns on row 1; content takes column 1 on row 2; the
 * details sidebar takes column 2 on row 2; the footer spans column 1
 * on row 3. From `md` up, Main pulls in with a margin and a
 * rounded border so it floats inside the page shell.
 *
 * Use exactly one Main inside each {@link Layout}. With
 * `type='simple'`, Layout renders Main for you.
 *
 * @example
 * <Layout>
 *   <Sidebar>{navItems}</Sidebar>
 *   <Main>
 *     <Header>{pageTitle}</Header>
 *     <Content>{pageBody}</Content>
 *   </Main>
 * </Layout>
 */
export function Main({ children }: MainProps) {
  return (
    <main className="isolate grid h-svh flex-1 grid-cols-[1fr_auto] grid-rows-[auto_1fr_auto] border-border-primary bg-bg-primary md:m-3 md:ml-0 md:h-[calc(100svh-1.5rem)] md:rounded-2xl md:border md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-3">
      {children}
    </main>
  );
}

/** Props for the {@link Header} component. */
export type HeaderProps = {
  /** Header content: page title, breadcrumbs, primary actions, sidebar trigger, etc. */
  children: React.ReactNode;
};

/**
 * Top region of a {@link Main}, fixed at 16 unit (4rem) height with a
 * bottom border. Spans both grid columns so it sits above both the
 * content area and the details sidebar.
 *
 * Header has no own props; it is positioned by Main's grid template.
 * Compose freely inside (use a {@link Stack} for horizontal toolbars).
 *
 * @example
 * <Header>
 *   <SidebarTrigger />
 *   <Heading size="xl" level={1} className="flex-1">Patients</Heading>
 *   <Stack direction="horizontal" gap={2} fullWidth={false}>
 *     <Button variant="outline">Filter</Button>
 *     <Button>New visit</Button>
 *   </Stack>
 * </Header>
 */
export function Header({ children }: HeaderProps) {
  return (
    <header className="col-span-2 row-start-1 flex h-16 items-center gap-2 border-b border-border-primary p-3">
      {children}
    </header>
  );
}

const contentStyles = tv({
  slots: {
    section: 'col-start-1 row-start-2 flex justify-center',
    container: 'w-full',
  },
  variants: {
    padded: {
      true: { container: 'p-3' },
    },
    size: {
      default: { container: 'max-w-none' }, // N/A
      sm: { container: 'max-w-160' }, // 640px
      xl: { container: 'max-w-7xl' }, // 1280px
    },
    scrollable: {
      true: { section: 'overflow-auto' },
      false: { section: 'overflow-hidden' },
    },
  },
  defaultVariants: {
    scrollable: true,
    size: 'default',
  },
});

type ContentStyleProps = VariantProps<typeof contentStyles>;

/**
 * Max-width tokens for the {@link Content} container.
 *
 * - `'default'`: no max width (`max-w-none`); content fills the column.
 * - `'sm'`: 640px (`max-w-160`); narrow column for forms or article body.
 * - `'xl'`: 1280px (`max-w-7xl`); wide column for dashboards.
 *
 * @default 'default'
 */
export type ContentSize = NonNullable<ContentStyleProps['size']>;

/**
 * Props for the {@link Content} component.
 */
export type ContentProps = {
  /** Page body. */
  children: React.ReactNode;
  /** When `true`, applies a `p-3` inset to the inner container. @default false */
  padded?: boolean;
  /** Max width of the inner container. See {@link ContentSize}. @default 'default' */
  size?: ContentSize;
  /**
   * Whether Content owns scrolling. Disable when a child layout provides the
   * page's single scroll region. @default true
   */
  scrollable?: boolean;
};

/**
 * Inner content region inside {@link Main}. Owns horizontal centering,
 * vertical scroll, and the typed `size` / `padded` props that constrain
 * the inner container.
 *
 * Two slots:
 * - The outer `<section>` lives in column 1, row 2 of Main's grid and
 *   handles scrolling for long pages by default. Set `scrollable={false}`
 *   when a child layout owns the page's single scroll region.
 * - The inner `<div>` is the constrained container: width comes from
 *   `size`, inset padding from `padded`. For responsive padding, layer
 *   `className` (`className="md:p-6 lg:p-8"`).
 *
 * Pages that bring their own padding (full-bleed editors, dashboards
 * with section-level padding) leave `padded` false. Pages with naked
 * content (settings forms, plain text) set it true.
 *
 * @example
 * // Reading column for an article or settings form.
 * <Content size="sm" padded>
 *   <ArticleBody />
 * </Content>
 *
 * @example
 * // Wide dashboard. For breakpoint-scaled padding, layer a wrapper since
 * // Content does not accept `className`.
 * <Content size="xl">
 *   <div className="p-3 md:p-6 lg:p-8">
 *     <Dashboard />
 *   </div>
 * </Content>
 */
export function Content({
  children,
  padded,
  size,
  scrollable = true,
}: ContentProps) {
  const { section, container } = contentStyles({ padded, size, scrollable });
  return (
    <section className={section()}>
      <div className={container()}>{children}</div>
    </section>
  );
}

/** Props for the {@link Footer} component. */
export type FooterProps = {
  /** Footer content: status text, attribution, secondary actions. */
  children: React.ReactNode;
};

/**
 * Bottom region of a {@link Main}, with a top border and minimum 16
 * unit (4rem) height. Sits in column 1 of row 3, beside any open
 * {@link DetailsSidebar} on the same row.
 *
 * Footer has no own props; it is positioned by Main's grid template.
 *
 * @example
 * <Footer>
 *   <Stack direction="horizontal" align="center" justify="between">
 *     <Text color="secondary">All times in UTC</Text>
 *     <Button variant="ghost" size="sm">Help</Button>
 *   </Stack>
 * </Footer>
 */
export function Footer({ children }: FooterProps) {
  return (
    <footer className="col-span-1 row-start-3 flex min-h-16 items-center border-t border-border-primary p-3">
      {children}
    </footer>
  );
}
